import { ICreatorPlatformStatusValue } from '~/clientApi';
import {
  INSTAGRAM_CONTENT_TYPES,
  PARTIALLY_SUPPORTED_PLATFORMS,
  SOCIAL_PLATFORMS,
} from '~/constants';
import { Thumbnails } from '~/screens/ContentSharedScreen/ContentSharedScreen.types';
import {
  Filter,
  MetaApiResponse,
  ORDER,
  YOUTUBE_ASSET_SIZE,
} from '~/searchApi';
import { ICreatorLocation } from '~/types';

export enum SORT {
  comments = 'comment_count',
  likes = 'like_count',
  mostRecent = 'published_at',
  sharedViews = 'shared_view_count',
  shares = 'share_count',
  relevance = 'relevance',
}

export enum ASSET_TYPE {
  image = 'image',
  video = 'video',
}

export interface ISearchContentParams {
  filters?: Filter[];
  managerId?: string;
  orderBy?: string;
  order?: ORDER;
  page?: number;
  pageSize?: number;
  platform?: string;
  productType?: string;
  searchQuery?: string;
  searchQueryFuzzyMatch?: boolean;
}

export interface IApiSearchAsset {
  height?: number;
  name?: YOUTUBE_ASSET_SIZE | null;
  thumbnail?: string;
  previewUrl?: string;
  type?: ASSET_TYPE;
  url: string;
  width?: number;
}

export interface ISearchContentPlatform {
  avatar?: string;
  handle?: string;
  name?: string;
  location?: ICreatorLocation;
}

export interface ISearchContentPlatforms {
  platforms: {
    [SOCIAL_PLATFORMS.instagram]?: ISearchContentPlatform;
    [SOCIAL_PLATFORMS.tiktok]?: ISearchContentPlatform;
    [SOCIAL_PLATFORMS.youtube]?: ISearchContentPlatform;
    [PARTIALLY_SUPPORTED_PLATFORMS.snapchat]?: ISearchContentPlatform;
  };
}

export interface ISearchContentCreator extends ISearchContentPlatforms {
  avatar?: string;
  email?: string;
  firstName?: string;
  fullName?: string;
  id: string;
  lastName?: string;
  name?: string;
  location?: ICreatorLocation;
  slug?: string;
  status: ICreatorPlatformStatusValue;
}

export type ContentPlatformStatus = 'on-platform' | 'off-platform';

export interface IApiSearchContent {
  assets: IApiSearchAsset[];
  caption: string;
  comments: number;
  id: string;
  contentId: string;
  createdTime: string;
  creator: ISearchContentCreator;
  engagements: number;
  externalUrl?: string;
  impressions?: number;
  likes: number;
  networkId?: string;
  platform: SOCIAL_PLATFORMS;
  publishedAt: string;
  shareLink?: string;
  shares: number;
  shareUrl?: string;
  type: ASSET_TYPE;
  updatedAt: string;
  insightLastUpdatedAt: string;
  views: number;
  status?: ContentPlatformStatus; // FOAM-13846 - we decided to add `status` field to content. This is mandatory in krill-base-api but not sure about search api
}

export interface SearchContentVmResponse {
  data: ISearchContentResult[];
  meta: MetaApiResponse;
}

export interface IApiSearchInstagramContent extends IApiSearchContent {
  channel: INSTAGRAM_CONTENT_TYPES;
  interactions: number;
  platform: SOCIAL_PLATFORMS.instagram;
  plays: number;
  igReelsAggregatedAllPlaysCount?: number;
  postFollowersEngRate: number;
  postReachEngRate: number;
  reach: number;
  saved: number;
  shareUrl?: string;
  type: ASSET_TYPE;
  publishedAt: string;
  viewsEngRate?: number;
  postedAt: string;
  replies?: number;
  isCollaboration?: boolean;
  viewsCount?: number;
  productType?: string;
  contentType?: string;
}

export interface IApiSearchTiktokContent extends IApiSearchContent {
  embedUrl: string;
  engagementRate: number;
  externalUrl?: string;
  followerEngagementRate: number;
  platform: SOCIAL_PLATFORMS.tiktok;
  type: ASSET_TYPE.video;
  createTime: string;
  engagementCount: number;
  videoViews: number;
}

export interface IApiSearchYoutubeContent extends IApiSearchContent {
  dislikes: number;
  favourites: number;
  networkId?: string;
  platform: SOCIAL_PLATFORMS.youtube;
  tags?: string;
  type: ASSET_TYPE.video;
  ytVideoId?: string;
  thumbnails: Thumbnails; //Compatiblity with shared content screen
  viewCount: number;
  likeCount: number;
  commentCount: number;
  dislikeCount: number;
  viewRate: number;
}

export type ISearchContentResult =
  | IApiSearchInstagramContent
  | IApiSearchTiktokContent
  | IApiSearchYoutubeContent;

export interface SearchHighlightedContentResult {
  id: string;
  platform: string;
}
